import {Dialog} from '@headlessui/react'
import {useBoolean} from 'usehooks-ts'
import {useCallback, useState} from 'react'
import {AnimatePresence} from 'framer-motion'

import {SlideshowScrim} from './Slideshow.Scrim'
import {SlideshowControls} from './Controls/Slideshow.Controls'
import {SlideshowContent} from './Slideshow.Content'
import {SlideshowContext} from './SlideshowContext'

import type {ReactNode} from 'react'
import type {ClassNameValue} from 'tailwind-merge'

type SlideshowRenderProps = {
  show: (index?: number) => void
  hide: () => void
}

export interface SlideshowItem {
  richText: string
  src: string
  thumbnailSrc?: string
  placeholderSrc?: string
  alt: string
  width?: number
  height?: number
}

export interface SlideshowProps {
  // accept render function or ReactNode als children
  children?: ((ctx: SlideshowRenderProps) => ReactNode) | ReactNode
  className?: ClassNameValue
  items: SlideshowItem[]
}

export const Slideshow = (props: SlideshowProps) => {
  const {children, items} = props
  const {setTrue: openDialog, setFalse: closeDialog, value: open} = useBoolean(false)
  const [index, setIndex] = useState(0)

  const ctx: SlideshowRenderProps = {
    show: (i = 0) => {
      openDialog()
      setIndex(i)
    },
    hide: () => {
      closeDialog()
    },
  }

  const showPrevious = useCallback(() => {
    setIndex((currentIndex) => {
      return Math.max(currentIndex - 1, 0)
    })
  }, [setIndex])

  const showNext = useCallback(() => {
    setIndex((currentIndex) => {
      return Math.min(currentIndex + 1, items.length - 1)
    })
  }, [setIndex, items.length])

  return (
    <SlideshowContext.Provider
      value={{
        showPrevious,
        showNext,
        showIndex: setIndex,
        currentIndex: index,
        items: items,
      }}
    >
      {typeof children === 'function' ? children(ctx) : children}
      <AnimatePresence>
        {open && (
          <Dialog
            open={open}
            onClose={closeDialog}
            className={'fixed inset-0 z-[100]'}
          >
            <SlideshowScrim>
              <div className={'grid grid-rows-[80px_calc(100dvh-80px)] text-glyph-default-primary md:grid-rows-[44px_calc(100dvh-140px)] md:gap-spacing-32'}>
                <SlideshowControls />
                <SlideshowContent />
              </div>
            </SlideshowScrim>
          </Dialog>
        )}
      </AnimatePresence>
    </SlideshowContext.Provider>
  )
}
